import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { captureException } from '@sentry/nextjs';
import styled from 'styled-components';
import { spacer12 } from '@spotify-internal/encore-web';
import { useProperty } from '@spotify-internal/remote-config-integration-react';

import { DocumentLink, QuickLink } from 'lib/sanity/types';
import analytics from 'utils/client/analytics/analytics';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import useShouldUseSemanticSearch from 'utils/semantic';
import { enableQuickLinksInSearch } from 'utils/remote-config/properties';
import { Vertical } from 'utils/vertical';
import { QuickLinksInSearch } from 'components/QuickLinks';

import { getMinRequiredChars } from './search-utils';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import hasFocus from './hasFocus';

const Container = styled.div<{ $renderAsOverlay: boolean }>`
  padding: ${spacer12} 0;
  ${({ $renderAsOverlay }) => $renderAsOverlay && `position: relative;`}
`;

interface SearchProps {
  ubi: UbiHomePage;
  quickLinks: QuickLink[];
  renderAsOverlay?: boolean;
}

const Search = ({ ubi, quickLinks, renderAsOverlay = false }: SearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentLink[]>([]);
  const [debouncedQuery] = useDebounce(searchQuery, 500);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const {
    verticalConfig: { verticalName },
    market: { marketCode, locale },
  } = useServerSideConstants();
  const showQuicklinksOnClickEnabled = useProperty(enableQuickLinksInSearch) && verticalName === Vertical.CONSUMER;
  const [showQuickLinks, setShowQuickLinks] = useState(false);
  const searchContainerRef = React.useRef<HTMLDivElement>(null);

  const shouldUseSemanticSearch = useShouldUseSemanticSearch();

  const minRequiredChars = getMinRequiredChars(locale);
  const enteredMinSearchChars = debouncedQuery.length >= minRequiredChars;

  useEffect(() => {
    const fetchData = async (query: string) => {
      setIsSearching(true);
      setShowQuickLinks(false);
      ubi.logSearch(query);

      const queryParamsObj: Record<string, string> = {
        q: query,
      };

      // remote-config doesn't work in API route, we have do it on the client side.
      if (shouldUseSemanticSearch) {
        // Make it implicit for not leaking info
        queryParamsObj.exp = '1';
      }

      const queryString = new URLSearchParams(queryParamsObj).toString();

      try {
        const response = await fetch(`/api/web/vertical/${verticalName}/${marketCode}/search/?${queryString}`);
        if (!response.ok) {
          throw new Error(`Search API Error with status: ${response.status.toString()}`);
        }
        analytics.trackVirtualPageView(`/search/?q=${query}`);
        const results = await response.json();

        setSearchResults(results);
        setIsSearching(false);
        setShowResults(true);
      } catch (error) {
        setSearchResults([]);
        setIsSearching(false);
        setShowResults(true);
        captureException(error);
      }
    };

    if (enteredMinSearchChars) {
      fetchData(debouncedQuery);
    } else if (!enteredMinSearchChars) {
      setSearchResults([]);
      setShowResults(false);

      if (showQuicklinksOnClickEnabled && debouncedQuery.length === 0 && hasFocus(searchContainerRef.current)) {
        // Show quick links again when the search input is focused and the query is empty
        setShowQuickLinks(true);
      }
    }
  }, [
    debouncedQuery,
    enteredMinSearchChars,
    marketCode,
    ubi,
    verticalName,
    shouldUseSemanticSearch,
    showQuicklinksOnClickEnabled,
  ]);

  useEffect(() => {
    if (showQuickLinks) {
      ubi.logQuickLinkContainerImpression();
    }
  }, [showQuickLinks, ubi]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  function onInputFocus() {
    if (showQuicklinksOnClickEnabled && !showResults) {
      setShowQuickLinks(true);
    }
  }

  return (
    <Container ref={searchContainerRef} $renderAsOverlay={renderAsOverlay}>
      <SearchInput
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        clearSearch={clearSearch}
        onFocus={onInputFocus}
      />
      {showResults && <SearchResults searchResults={searchResults} ubi={ubi} renderAsOverlay={renderAsOverlay} />}
      {showQuickLinks && <QuickLinksInSearch quickLinks={quickLinks} ubi={ubi} />}
    </Container>
  );
};

export default Search;
